const ALLOWED_TYPES = new Set<string>(['image/jpeg', 'image/png']);
const MAX_BYTES = 5 * 1024 * 1024;
const REQUIRED_DIMENSION = 192;

export type LogoValidationError =
  | { kind: 'invalid_type'; message: string }
  | { kind: 'too_large'; message: string }
  | { kind: 'wrong_dimension'; message: string }
  | { kind: 'load_failed'; message: string };

/**
 * Client-side gate before we upload a bad file. Validates type, size, and
 * exact 192×192 dimension by loading the file into an Image. The BFF
 * re-validates type and size; dimension is only checked client-side
 * (keeps the BFF off `sharp` — see Decision 10 in the original research).
 */
export async function validateLogoFile(
  file: File
): Promise<{ ok: true } | { ok: false; error: LogoValidationError }> {
  if (!ALLOWED_TYPES.has(file.type)) {
    return {
      ok: false,
      error: { kind: 'invalid_type', message: 'Envie um arquivo .JPG ou .PNG' },
    };
  }
  if (file.size > MAX_BYTES) {
    return {
      ok: false,
      error: { kind: 'too_large', message: 'O arquivo deve ter no máximo 5 MB' },
    };
  }
  const dims = await readImageDimensions(file).catch((err) => err as Error);
  if (dims instanceof Error) {
    return {
      ok: false,
      error: { kind: 'load_failed', message: 'Não foi possível ler a imagem' },
    };
  }
  if (dims.width !== REQUIRED_DIMENSION || dims.height !== REQUIRED_DIMENSION) {
    return {
      ok: false,
      error: {
        kind: 'wrong_dimension',
        message: `A logo deve ter dimensão exata de 192 × 192 px (recebido ${dims.width} × ${dims.height})`,
      },
    };
  }
  return { ok: true };
}

function readImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const width = img.naturalWidth;
      const height = img.naturalHeight;
      URL.revokeObjectURL(url);
      resolve({ width, height });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('image load failed'));
    };
    img.src = url;
  });
}
